import { Option } from '@sapphire/result';

import { BasePayloadMapper } from '@ps/utils';
import {
  AdvancedTargetingDefinition,
  EMPTY_FEASIBILITY_EXCLUSIONS,
  FeasibilityExclusions,
  RESPONDENT_VALID_CLICK_CODES,
  SurveyMetadataForm,
  SurveyPricingSnapshot,
  SurveyQualificationTab,
} from '@ps/survey-utils';

import { FeasibilityExclusionsDto } from '../dtos/feasibility-exclusion.dto';
import { QualificationPayloadDto } from '../dtos/survey-qualification.dto';
import { QuotaPayloadDto } from '../dtos/survey-quota.dto';
import { StandardSurveyPayloadDto } from '../dtos/survey.dto';
import { SURVEY_CATEGORY_CODE } from '../../utils';
import { QuotaPayloadMapper } from './quota.payload.mapper';

export interface StandardSurveyTargetingInput {
  definitions: AdvancedTargetingDefinition[];
  locationSelectionsByQualificationCode: Record<number, string[]>;
  qualifications: SurveyQualificationTab[];
}

export interface StandardSurveyPayloadInput {
  metadata: SurveyMetadataForm;
  companyId: number;
  userId: number;
  pricing: SurveyPricingSnapshot;
  targeting?: StandardSurveyTargetingInput;
  feasibilityExclusions?: FeasibilityExclusions;
  hasRespondentStatusFilterEnabled?: boolean;
  hasExclusion?: boolean;
}

export class StandardSurveyPayloadMapper extends BasePayloadMapper<
  StandardSurveyPayloadInput,
  StandardSurveyPayloadDto
> {
  private readonly _quotaMapper = new QuotaPayloadMapper();

  public toPayload({
    metadata,
    companyId,
    userId,
    pricing,
    targeting,
    feasibilityExclusions,
    hasRespondentStatusFilterEnabled,
    hasExclusion,
  }: StandardSurveyPayloadInput): StandardSurveyPayloadDto {
    const payload: StandardSurveyPayloadDto = {
      survey_title: metadata.title,
      buyer_company: companyId,
      buyer_user_id: userId,
      survey_category_code: SURVEY_CATEGORY_CODE,
      survey_localization: this._localization(metadata),
      completes_required: metadata.completes ?? 0,
      expected_loi: metadata.loi ?? 0,
      expected_ir: metadata.incidence ?? 0,
      field_time: metadata.fieldTime ?? 0,
      click_balancing: this._clickBalancing(metadata),
      feasibility_exclusions: this._feasibilityExclusions(
        feasibilityExclusions,
        hasRespondentStatusFilterEnabled,
        hasExclusion,
      ),
      feasibility_cpi: pricing.feasibilityCpi,
      blend_applied: pricing.blendApplied,
    };

    const qualifications = this._qualifications(targeting);
    const quotas = this._quotas(targeting);

    return {
      ...payload,
      ...Option.from(pricing.surveyPauseThreshold).mapOr({}, (value) => ({
        survey_pause_threshold: value,
      })),
      ...Option.from(pricing.offerPrice).mapOr({}, (offerPrice) => ({
        offer_price: offerPrice,
      })),
      ...(qualifications.length ? { qualifications } : {}),
      ...(quotas.length ? { quotas } : {}),
    };
  }

  private _localization(metadata: SurveyMetadataForm): string {
    const countryCode = metadata.selectedCountries[0];
    const languageShortCode = metadata.selectedLanguages[0];
    if (!countryCode || !languageShortCode) {
      return '';
    }
    return `${languageShortCode}_${countryCode}`;
  }

  private _clickBalancing(metadata: SurveyMetadataForm): number {
    return metadata.clickBalancing ? 1 : 0;
  }

  private _feasibilityExclusions(
    exclusions: FeasibilityExclusions = EMPTY_FEASIBILITY_EXCLUSIONS,
    hasRespondentStatusFilterEnabled = false,
    hasExclusion = true,
  ): FeasibilityExclusionsDto {
    return {
      enable: exclusions.enabled,
      start: exclusions.start,
      end: exclusions.end,
      criterias: exclusions.criteria.map((criteria) => ({
        id: criteria.id,
        name: criteria.name,
        values: criteria.values.map((value) => ({
          id: value.id,
          name: value.name,
        })),
      })),
      criteriaSurveys: exclusions.criteriaSurveys,
      ...this._feasibilityExclusionStatuses(
        exclusions,
        hasRespondentStatusFilterEnabled,
        hasExclusion,
      ),
    };
  }

  private _feasibilityExclusionStatuses(
    exclusions: FeasibilityExclusions = EMPTY_FEASIBILITY_EXCLUSIONS,
    hasRespondentStatusFilterEnabled = false,
    hasExclusion = true,
  ): Pick<FeasibilityExclusionsDto, 'statuses'> {
    if (!hasRespondentStatusFilterEnabled || !hasExclusion) {
      return {};
    }
    return {
      statuses: exclusions.statuses.length
        ? exclusions.statuses
        : RESPONDENT_VALID_CLICK_CODES,
    };
  }

  // Advance Targeting entries precede Location entries, matching the order the
  // legacy payload builder produced.
  private _qualifications(
    targeting: StandardSurveyTargetingInput | undefined,
  ): QualificationPayloadDto[] {
    if (!targeting) {
      return [];
    }

    return [
      ...targeting.definitions.map((definition) =>
        this._advancedTargetingQualification(definition),
      ),
      ...this._locationQualifications(targeting),
    ];
  }

  private _advancedTargetingQualification(
    definition: AdvancedTargetingDefinition,
  ): QualificationPayloadDto {
    const payload: QualificationPayloadDto = {
      qualification_name: definition.title,
      qualification_code: Number(definition.attributeId),
      condition_codes: definition.selectedOptionIds,
    };

    // A range definition can only have been hydrated from a saved survey — the
    // drawer cannot author one. Re-emitting its range set keeps the PUT, which
    // replaces qualifications wholesale, from deleting it.
    if (!definition.rangeSet) {
      return payload;
    }

    return { ...payload, range_sets: [definition.rangeSet] };
  }

  // Sibling of qualifications, not nested inside it. Recomputed from every definition's
  // applied quotas each save, matching qualifications' whole-array-replace semantics.
  private _quotas(
    targeting: StandardSurveyTargetingInput | undefined,
  ): QuotaPayloadDto[] {
    if (!targeting) {
      return [];
    }

    return targeting.definitions
      .flatMap((definition) => definition.quotas ?? [])
      .map((quota) => this._quotaMapper.toPayload(quota));
  }

  private _locationQualifications({
    locationSelectionsByQualificationCode,
    qualifications,
  }: StandardSurveyTargetingInput): QualificationPayloadDto[] {
    return qualifications
      .filter((qualification) => qualification.isLocation)
      .map((qualification) => ({
        qualification,
        selectedOptionIds:
          locationSelectionsByQualificationCode[
            qualification.qualificationCode
          ] ?? [],
      }))
      .filter(({ selectedOptionIds }) => selectedOptionIds.length > 0)
      .map(({ qualification, selectedOptionIds }) => ({
        qualification_name: qualification.label,
        qualification_code: qualification.qualificationCode,
        condition_codes: selectedOptionIds,
      }));
  }
}
